import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MikaCard from '../../components/MikaCard/MikaCard';
import { formatPrice } from '../../utils/react';

interface ArchivedProductCardGridProps {
    data: any[];
    onViewFiles: (product: any) => void;
}

const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.charAt(0) + (parts[1]?.charAt(0) || '')).toUpperCase();
};

const getAvatarColor = (name: string): string => {
    const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#6366f1'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

const ArchivedProductCardGrid: React.FC<ArchivedProductCardGridProps> = ({ data, onViewFiles }) => {
    const intl = useIntl();
    const viewFilesTooltip = intl.formatMessage({ id: 'production.productArchive.tooltip.viewFiles', defaultMessage: 'Skedarët' });

    return (
        <div className="ccg-grid">
            {data.length === 0 ? (
                <div className="ccg-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                        <path d="M2 20h20" /><path d="M5 20V10l7-7 7 7v10" /><path d="M9 20v-6h6v6" />
                    </svg>
                    <p><FormattedMessage id="production.productArchive.empty" defaultMessage="Nuk u gjet asnjë produkt" /></p>
                </div>
            ) : (
                data.map((product, idx) => {
                    const initials = getInitials(product.clientName || '');
                    const avatarColor = getAvatarColor(product.clientName || '');

                    return (
                        <MikaCard
                            key={idx}
                            onClick={() => onViewFiles(product)}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: avatarColor }}>
                                        {initials}
                                    </div>
                                    <div className="ccg-card-name">
                                        <span className="ccg-name">{product.productName || '\u2014'}</span>
                                        <span className="ccg-company">{product.clientName}</span>
                                    </div>
                                    <span className="ccg-status-badge" style={{ color: '#22c55e', background: '#dcfce7' }}>
                                        <FormattedMessage id="production.archive.completed" defaultMessage="E përfunduar" />
                                    </span>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></span>
                                            <span className="ccg-info-value">{product.projectName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.productArchive.modal.duration" defaultMessage="Kohëzgjatja" /></span>
                                            <span className="ccg-info-value">{product.duration || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.start" defaultMessage="Fillimi" /></span>
                                            <span className="ccg-info-value">{product.trackersStart || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.end" defaultMessage="Mbarimi" /></span>
                                            <span className="ccg-info-value">{product.trackersEnd || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.quantity" defaultMessage="Sasia" /></span>
                                            <span className="ccg-info-value">{product.quantity || '\u2014'}</span>
                                        </div>
                                        {product.price && (
                                            <div className="ccg-info-item">
                                                <span className="ccg-info-label"><FormattedMessage id="production.detail.value" defaultMessage="Vlera" /></span>
                                                <span className="ccg-info-value" style={{ color: '#1e40af', fontWeight: 600 }}>{formatPrice(product.price, '€')}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date" style={{ color: '#64748b', fontSize: 11 }}>
                                        {product.trackersEnd
                                            ? <FormattedMessage id="production.productArchive.completedAt" defaultMessage="Përfunduar: {date}" values={{ date: product.trackersEnd }} />
                                            : ''}
                                    </span>
                                    <div className="ccg-footer-actions">
                                        <button className="ccg-action-btn" onClick={(e) => { e.stopPropagation(); onViewFiles(product); }} title={viewFilesTooltip}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            }
                        />
                    );
                })
            )}
        </div>
    );
};

export default ArchivedProductCardGrid;
